import AddCircleOutline from '@mui/icons-material/AddCircleOutline'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import Close from '@mui/icons-material/Close'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, IconButton, Tooltip, useTheme, useMediaQuery, Collapse, Typography, Chip } from '@mui/material'
import dayjs from 'dayjs'
import {
  DIAGNOSIS_SUB_SECTION_IDS,
  DIAGNOSIS_TABLE_COLUMN_WEIGHTS,
  MOMENT_FORMATS,
} from 'src/consumer/constants'
import { getPastDateFromTimeString } from 'src/mentor/helpers/utilityFunctions'
import {
  commonStylesPrescriptionSelect,
  TextAreaInputTextAreaInputForEMR,
} from '../../../PrescriptionMakingPageComponents'
import {
  DiagnosisCreatableSelect,
  DiagnosisDurationSelect,
  PMPSingleRowBox,
  PMPSingleRowForDiagnosis,
} from '../../../PrescriptionMakingPageStyles'
import { handleChangeHealthEntityInputTypeForDiagnosisTable } from '../../common/helpers'
import {
  getRenderDiagnosisSubSectionsSet,
  INITIAL_DIAGNOSIS_OPTION_ARRAY_LIST,
  loadOptionsDiagnosisDuration,
} from '../util'
import DayjsDatePicker from './DayjsDatePicker'
import { useEffect, useState } from 'react'

export default function DiagnosisTabularSubSectionsInput({
  diagnosisList,
  subSections,
  onChangeDiagnosis,
  loadOptionsDiagnosis,
  clinicId,
  clinicBrandId,
  recommendedDiagnosisIds,
  prescriptionViewSettings,
  menuPortalTarget,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  
  // For mobile: track which cards are expanded
  const [expandedCards, setExpandedCards] = useState({})

  const toggleCardExpansion = (index) => {
    setExpandedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  // Auto-expand card when diagnosis is selected
  useEffect(() => {
    if (isMobile) {
      diagnosisList.forEach((diagnosisItem, index) => {
        const hasDiagnosis = Boolean(diagnosisItem?.value || diagnosisItem?.label)
        if (hasDiagnosis && !expandedCards[index]) {
          setExpandedCards((prev) => ({
            ...prev,
            [index]: true,
          }))
        }
      })
    }
  }, [diagnosisList, isMobile])

  function changeDiagnosisItemObject(val, idx, updateWholeObject = false) {
    onChangeDiagnosis(
      diagnosisList.map((diagnosis, index) =>
        index === idx ? { ...(updateWholeObject ? {} : diagnosis), ...val } : diagnosis,
      ),
    )
  }

  function handleChangeDiagnosis(val, changeField, idx) {
    onChangeDiagnosis(
      diagnosisList.map((diagnosis, index) =>
        index === idx ? { ...diagnosis, [changeField]: val } : diagnosis,
      ),
    )
  }

  function addDiagnosis() {
    onChangeDiagnosis([...diagnosisList, {}])
  }

  function deleteDiagnosis(idx) {
    onChangeDiagnosis(diagnosisList.filter((_, index) => index !== idx))
  }

  // Mobile Card View
  if (isMobile) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {diagnosisList?.map((diagnosisItem, diagnosisRowIndex) => {
          const hasDiagnosis = Boolean(diagnosisItem?.value || diagnosisItem?.label)
          const isExpanded = expandedCards[diagnosisRowIndex] ?? hasDiagnosis

          return (
            <Box
              key={diagnosisRowIndex}
              sx={{
                borderRadius: '16px',
                overflow: 'visible',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f7ff 100%)',
                border: hasDiagnosis ? '2px solid #4338ca' : '1px solid rgba(67, 56, 202, 0.15)',
                boxShadow: hasDiagnosis
                  ? '0 4px 20px rgba(67, 56, 202, 0.15)'
                  : '0 2px 8px rgba(67, 56, 202, 0.06)',
                transition: 'all 0.3s ease',
                animation: 'fadeSlideIn 0.3s ease-out',
                position: 'relative',
                zIndex: isExpanded ? 1000 + (100 - diagnosisRowIndex) : 1,
                '@keyframes fadeSlideIn': {
                  from: {
                    opacity: 0,
                    transform: 'translateY(10px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {/* Card Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  background: hasDiagnosis
                    ? 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)'
                    : 'transparent',
                  borderBottom: hasDiagnosis ? '1px solid rgba(67, 56, 202, 0.1)' : 'none',
                }}
              >
                {/* Diagnosis Number Badge */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '10px',
                    background: hasDiagnosis
                      ? 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)'
                      : 'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 14,
                    flexShrink: 0,
                    boxShadow: hasDiagnosis
                      ? '0 2px 8px rgba(67, 56, 202, 0.3)'
                      : '0 1px 4px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {diagnosisRowIndex + 1}
                </Box>

                {/* Diagnosis Name Field - Full Width */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {getRenderDiagnosisSubSectionsSet(subSections).has(
                    DIAGNOSIS_SUB_SECTION_IDS.diagnosis_title.id,
                  ) && (
                    <DiagnosisTableItem
                      diagnosisList={diagnosisList}
                      diagnosisRowIndex={diagnosisRowIndex}
                      subSections={subSections}
                      loadOptionsDiagnosis={loadOptionsDiagnosis}
                      diagnosisItem={diagnosisItem}
                      changeDiagnosisItemObject={changeDiagnosisItemObject}
                      clinicId={clinicId}
                      clinicBrandId={clinicBrandId}
                      recommendedDiagnosisIds={recommendedDiagnosisIds}
                      prescriptionViewSettings={prescriptionViewSettings}
                      handleChangeDiagnosis={handleChangeDiagnosis}
                      deleteDiagnosis={deleteDiagnosis}
                      menuPortalTarget={menuPortalTarget}
                      isMobile={true}
                      showOnlyDiagnosisTitle={true}
                    />
                  )}
                </Box>

                {/* Expand/Collapse & Delete Buttons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {hasDiagnosis && (
                    <IconButton
                      onClick={() => toggleCardExpansion(diagnosisRowIndex)}
                      size="small"
                      sx={{
                        color: '#4338ca',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(67, 56, 202, 0.1)',
                        },
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={() => deleteDiagnosis(diagnosisRowIndex)}
                    size="small"
                    sx={{
                      color: '#ef4444',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      },
                    }}
                  >
                    <DeleteOutline sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Expandable Content - Diagnosis Details */}
              <Collapse in={isExpanded && hasDiagnosis}>
                <Box
                  sx={{
                    p: 2,
                    pt: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {/* Date Field */}
                  {getRenderDiagnosisSubSectionsSet(subSections).has(
                    DIAGNOSIS_SUB_SECTION_IDS.diagnosis_since_date.id,
                  ) && (
                    <MobileFieldWrapper
                      label={subSections?.find(
                        (item) => item?.uid === DIAGNOSIS_SUB_SECTION_IDS.diagnosis_since_date.id,
                      )?.title || 'Since Date'}
                      color="#f59e0b"
                      fullWidth
                    >
                      <div className="d-flex align-items-center w-100 gap-1 position-relative">
                        <DayjsDatePicker
                          isDisabled={!diagnosisItem?.value}
                          buttonProps={{
                            startIcon: <CalendarMonth sx={{ fontSize: 18 }} />,
                            sx: {
                              width: '100%',
                              borderRadius: '6px',
                              variant: 'text',
                              color: !!diagnosisItem?.date ? '#1e293b' : '#9ca3af',
                              border: '1px solid rgba(245, 158, 11, 0.2)',
                              textTransform: 'none',
                              fontSize: '14px',
                              fontWeight: 500,
                              justifyContent: 'flex-start',
                              px: 1.5,
                              py: 1,
                              '&:hover': {
                                borderColor: 'rgba(245, 158, 11, 0.4)',
                                backgroundColor: 'rgba(245, 158, 11, 0.05)',
                              },
                              transition: 'all 0.2s ease',
                            },
                            disableRipple: false,
                          }}
                          placeholder="Date"
                          date={diagnosisItem?.date}
                          onChange={(val) => {
                            handleChangeDiagnosis(
                              dayjs(val).format(MOMENT_FORMATS.formatThatServerSends),
                              'date',
                              diagnosisRowIndex,
                            )
                          }}
                        />
                        {diagnosisItem?.date && (
                          <Close
                            sx={{
                              position: 'absolute',
                              right: '10px',
                              zIndex: 2,
                              cursor: 'pointer',
                              color: '#CCC',
                              fontSize: '16px',
                            }}
                            onClick={() => handleChangeDiagnosis(null, 'date', diagnosisRowIndex)}
                          />
                        )}
                      </div>
                    </MobileFieldWrapper>
                  )}

                  {/* Duration Field */}
                  {getRenderDiagnosisSubSectionsSet(subSections).has(
                    DIAGNOSIS_SUB_SECTION_IDS.diagnosis_for_duration.id,
                  ) && (
                    <MobileFieldWrapper
                      label={subSections?.find((item) => item?.uid === 'diagnosis_for_duration')?.title || 'Duration'}
                      color="#ec4899"
                      fullWidth
                    >
                      <DiagnosisDurationSelect
                        id="diagnosis_for_duration"
                        isDisabled={!diagnosisItem?.value}
                        classNamePrefix="diagnosis_for_duration_selection"
                        placeholder="Duration"
                        isSearchable={true}
                        isClearable={true}
                        defaultOptions={false}
                        loadOptions={loadOptionsDiagnosisDuration}
                        getOptionValue={(option) => option?.id}
                        getOptionLabel={(option) => option?.title}
                        isOptionDisabled={(option) =>
                          INITIAL_DIAGNOSIS_OPTION_ARRAY_LIST?.map((o) => o?.id).includes(option?.id)
                        }
                        value={
                          diagnosisItem?.duration
                            ? { id: diagnosisItem?.duration, title: diagnosisItem?.duration }
                            : null
                        }
                        onChange={(val) => {
                          if (!val) {
                            changeDiagnosisItemObject(
                              {
                                duration: val?.id,
                              },
                              diagnosisRowIndex,
                            )
                            return
                          }
                          const pastDate = getPastDateFromTimeString(val?.id)
                          changeDiagnosisItemObject(
                            {
                              duration: val?.id,
                              date: dayjs(pastDate).format(MOMENT_FORMATS.formatThatServerSends),
                            },
                            diagnosisRowIndex,
                          )
                        }}
                        additional={{}}
                        styles={{
                          ...commonStylesPrescriptionSelect,
                          menuPortal: (provided, state) => ({
                            ...provided,
                            zIndex: 9999,
                          }),
                          menu: (provided, state) => ({
                            ...provided,
                            ...commonStylesPrescriptionSelect.menu(provided, state),
                            zIndex: 9999,
                            position: 'absolute',
                          }),
                        }}
                        menuPortalTarget={menuPortalTarget || (typeof document !== 'undefined' ? document.body : null)}
                        menuPosition={menuPortalTarget ? 'fixed' : undefined}
                        components={{ ClearIndicator: () => null }}
                      />
                    </MobileFieldWrapper>
                  )}
                </Box>
              </Collapse>
            </Box>
          )
        })}

        {/* Add New Diagnosis Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 2,
            borderTop: '1px solid rgba(67, 56, 202, 0.1)',
            background: 'rgba(67, 56, 202, 0.02)',
            borderRadius: '12px',
          }}
        >
          <Tooltip title="Add new diagnosis" arrow>
            <IconButton
              onClick={() => {
                addDiagnosis()
              }}
              sx={{
                color: '#4338ca',
                '&:hover': {
                  backgroundColor: 'rgba(67, 56, 202, 0.1)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <AddCircleOutline sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    )
  }

  // Desktop Table View
  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid rgba(67, 56, 202, 0.12)',
        background: 'white',
        boxShadow: '0 2px 8px rgba(67, 56, 202, 0.06)',
      }}
    >
      {/* default row */}
      <PMPSingleRowForDiagnosis isHeaderRow useGreyBorders>
        <PMPSingleRowBox style={{ width: '3%' }} centered borderRight isHeaderRow>
          #
        </PMPSingleRowBox>
        {/* do not change the order. or improve so that order does not matter */}
        {getRenderDiagnosisSubSectionsSet(subSections).has(
          DIAGNOSIS_SUB_SECTION_IDS.diagnosis_title.id,
        ) && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDiagnosisTableCellWidth(
                DIAGNOSIS_SUB_SECTION_IDS.diagnosis_title.id,
                94,
                getRenderDiagnosisSubSectionsSet(subSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {
              subSections?.find(
                (item) => item?.uid === DIAGNOSIS_SUB_SECTION_IDS.diagnosis_title.id,
              )?.title
            }
          </PMPSingleRowBox>
        )}
        {getRenderDiagnosisSubSectionsSet(subSections).has(
          DIAGNOSIS_SUB_SECTION_IDS.diagnosis_since_date.id,
        ) && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDiagnosisTableCellWidth(
                DIAGNOSIS_SUB_SECTION_IDS.diagnosis_since_date.id,
                94,
                getRenderDiagnosisSubSectionsSet(subSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {
              subSections?.find(
                (item) => item?.uid === DIAGNOSIS_SUB_SECTION_IDS.diagnosis_since_date.id,
              )?.title
            }
          </PMPSingleRowBox>
        )}
        {getRenderDiagnosisSubSectionsSet(subSections).has(
          DIAGNOSIS_SUB_SECTION_IDS.diagnosis_for_duration.id,
        ) && (
          <PMPSingleRowBox
            style={{
              width: getPercentageOfItemForDiagnosisTableCellWidth(
                DIAGNOSIS_SUB_SECTION_IDS.diagnosis_for_duration.id,
                94,
                getRenderDiagnosisSubSectionsSet(subSections),
              ),
            }}
            borderRight
            isHeaderRow
          >
            {subSections?.find((item) => item?.uid === 'diagnosis_for_duration')?.title}
          </PMPSingleRowBox>
        )}
        <div style={{ width: '3%' }}></div>
      </PMPSingleRowForDiagnosis>

      {/* to be rendered a number of times */}
      {diagnosisList?.map((diagnosisItem, diagnosisRowIndex) => (
        <DiagnosisTableItem
          key={diagnosisRowIndex}
          changeDiagnosisItemObject={changeDiagnosisItemObject}
          clinicBrandId={clinicBrandId}
          clinicId={clinicId}
          deleteDiagnosis={deleteDiagnosis}
          diagnosisItem={diagnosisItem}
          diagnosisList={diagnosisList}
          diagnosisRowIndex={diagnosisRowIndex}
          handleChangeDiagnosis={handleChangeDiagnosis}
          loadOptionsDiagnosis={loadOptionsDiagnosis}
          prescriptionViewSettings={prescriptionViewSettings}
          recommendedDiagnosisIds={recommendedDiagnosisIds}
          subSections={subSections}
          menuPortalTarget={menuPortalTarget}
        />
      ))}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 2,
          borderTop: '1px solid rgba(67, 56, 202, 0.1)',
          background: 'rgba(67, 56, 202, 0.02)',
        }}
      >
        <Tooltip title="Add new diagnosis" arrow>
          <IconButton
            onClick={() => {
              addDiagnosis()
            }}
            sx={{
              color: '#4338ca',
              '&:hover': {
                backgroundColor: 'rgba(67, 56, 202, 0.1)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <AddCircleOutline sx={{ fontSize: 28 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

// Mobile Field Wrapper Component
const MobileFieldWrapper = ({ label, color, fullWidth, children }) => (
  <Box
    sx={{
      flex: fullWidth ? '1 1 100%' : '1 1 calc(50% - 6px)',
      minWidth: fullWidth ? '100%' : 0,
      position: 'relative',
    }}
  >
    <Box
      component="div"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: color,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        mb: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      {label}
    </Box>
    <Box
      sx={{
        borderRadius: '10px',
        overflow: 'visible',
        border: `1px solid ${color}20`,
        background: '#fff',
        transition: 'all 0.2s ease',
        '&:focus-within': {
          borderColor: color,
          boxShadow: `0 0 0 2px ${color}15`,
        },
        '& .css-1nmdiq5-menu, & [class*="-menu"]': {
          zIndex: 9999,
        },
      }}
    >
      {children}
    </Box>
  </Box>
)

function DiagnosisTableItem({
  diagnosisList,
  diagnosisRowIndex,
  subSections,
  loadOptionsDiagnosis,
  diagnosisItem,
  changeDiagnosisItemObject,
  clinicId,
  clinicBrandId,
  recommendedDiagnosisIds,
  prescriptionViewSettings,
  handleChangeDiagnosis,
  deleteDiagnosis,
  menuPortalTarget,
  isMobile = false,
  showOnlyDiagnosisTitle = false,
}) {
  const [inputValue, setInputValue] = useState()

  function setDefaultInputValue() {
    if (diagnosisItem?.value) {
      setInputValue(diagnosisItem?.label)
    } else {
      setInputValue('')
    }
  }

  useEffect(() => {
    setDefaultInputValue()
  }, [diagnosisItem])

  // If mobile and only showing diagnosis title, render simplified version
  if (isMobile && showOnlyDiagnosisTitle) {
    return (
      <DiagnosisCreatableSelect
        id="diagnosisSelection"
        alwaysShowInput
        classNamePrefix="diagnosis_table_selection"
        placeholder="Diagnosis"
        createOptionPosition="first"
        isSearchable={true}
        isClearable={true}
        defaultOptions={false}
        inputValue={inputValue}
        controlShouldRenderValue={false}
        loadOptions={loadOptionsDiagnosis}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        value={!!diagnosisItem?.value ? diagnosisItem : null}
        onChange={(val) => {
          if (!val) {
            changeDiagnosisItemObject(
              {
                title: '',
                duration: '',
                date: '',
                value: '',
              },
              diagnosisRowIndex,
              true,
            )
            setInputValue('')
            return
          }
          changeDiagnosisItemObject(val, diagnosisRowIndex)
          setInputValue(val?.label)
        }}
        debounceTimeout={200}
        additional={{
          page: 1,
          clinicId: clinicId,
          clinicBrandId: clinicBrandId,
          recommendedDiagnosisIds,
          prescriptionViewSettings,
        }}
        styles={{
          ...commonStylesPrescriptionSelect,
          menuPortal: (provided, state) => ({
            ...provided,
            zIndex: 9999,
          }),
          menu: (provided, state) => ({
            ...provided,
            ...commonStylesPrescriptionSelect.menu(provided, state),
            zIndex: 9999,
            position: 'absolute',
          }),
        }}
        menuPortalTarget={menuPortalTarget || (typeof document !== 'undefined' ? document.body : null)}
        menuPosition={menuPortalTarget ? 'fixed' : undefined}
        onInputChange={(value, actionMeta) => {
          if (actionMeta.action === 'input-blur') {
            setDefaultInputValue()
          }
          if (actionMeta.action === 'input-change') {
            setInputValue(value)
          }
          handleChangeHealthEntityInputTypeForDiagnosisTable(
            value,
            actionMeta,
            changeDiagnosisItemObject,
            diagnosisRowIndex,
          )
        }}
        components={{
          ClearIndicator: () => null,
          Input: TextAreaInputTextAreaInputForEMR,
          Placeholder: () => null,
        }}
      />
    )
  }

  return (
    <PMPSingleRowForDiagnosis
      useGreyBorders
      isLastRow={diagnosisRowIndex + 1 === diagnosisList?.length}
    >
      {/* do not change the order. or improve so that order does not matter */}
      <PMPSingleRowBox style={{ width: '3%' }} centered>
        {diagnosisRowIndex + 1}
      </PMPSingleRowBox>

      {getRenderDiagnosisSubSectionsSet(subSections).has(
        DIAGNOSIS_SUB_SECTION_IDS.diagnosis_title.id,
      ) && (
        <PMPSingleRowBox
          whiteBG
          borderRight
          noPadding
          noFlex
          style={{
            width: getPercentageOfItemForDiagnosisTableCellWidth(
              DIAGNOSIS_SUB_SECTION_IDS.diagnosis_title.id,
              94,
              getRenderDiagnosisSubSectionsSet(subSections),
            ),
          }}
        >
          <DiagnosisCreatableSelect
            id="diagnosisSelection"
            alwaysShowInput
            classNamePrefix="diagnosis_table_selection"
            placeholder="Diagnosis"
            createOptionPosition="first"
            isSearchable={true}
            isClearable={true}
            defaultOptions={false}
            inputValue={inputValue}
            controlShouldRenderValue={false}
            loadOptions={loadOptionsDiagnosis}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            value={!!diagnosisItem?.value ? diagnosisItem : null}
            onChange={(val) => {
              if (!val) {
                changeDiagnosisItemObject(
                  {
                    title: '',
                    duration: '',
                    date: '',
                    value: '',
                  },
                  diagnosisRowIndex,
                  true,
                )
                setInputValue('')
                return
              }
              changeDiagnosisItemObject(val, diagnosisRowIndex)
              setInputValue(val?.label)
              // handleChangeDiagnosis(val, 'diagnosis', diagnosisRowIndex)
            }}
            debounceTimeout={200}
            additional={{
              page: 1,
              clinicId: clinicId,
              clinicBrandId: clinicBrandId,
              recommendedDiagnosisIds,
              prescriptionViewSettings,
            }}
            styles={{
              ...commonStylesPrescriptionSelect,
              menuPortal: (provided, state) => ({
                ...provided,
                zIndex: 9999,
              }),
              menu: (provided, state) => ({
                ...provided,
                ...commonStylesPrescriptionSelect.menu(provided, state),
                zIndex: 9999, // Ensure menu has high z-index
                position: 'absolute', // Ensure proper positioning
              }),
            }}
            menuPortalTarget={menuPortalTarget || (typeof document !== 'undefined' ? document.body : null)}
            menuPosition={menuPortalTarget ? 'fixed' : undefined}
            onInputChange={(value, actionMeta) => {
              if (actionMeta.action === 'input-blur') {
                setDefaultInputValue()
              }
              if (actionMeta.action === 'input-change') {
                setInputValue(value)
              }
              handleChangeHealthEntityInputTypeForDiagnosisTable(
                value,
                actionMeta,
                changeDiagnosisItemObject,
                diagnosisRowIndex,
              )
            }}
            components={{
              ClearIndicator: () => null,
              Input: TextAreaInputTextAreaInputForEMR,
              Placeholder: () => null,
            }}
          />
        </PMPSingleRowBox>
      )}
      {getRenderDiagnosisSubSectionsSet(subSections).has(
        DIAGNOSIS_SUB_SECTION_IDS.diagnosis_since_date.id,
      ) && (
        <PMPSingleRowBox
          whiteBG
          borderRight
          noFlex
          noPadding
          style={{
            width: getPercentageOfItemForDiagnosisTableCellWidth(
              DIAGNOSIS_SUB_SECTION_IDS.diagnosis_since_date.id,
              94,
              getRenderDiagnosisSubSectionsSet(subSections),
            ),
          }}
        >
          <div className="d-flex align-items-center w-100 gap-1 position-relative">
            <DayjsDatePicker
              isDisabled={!diagnosisItem?.value}
              buttonProps={{
                startIcon: <CalendarMonth sx={{ fontSize: 18 }} />,
                sx: {
                  width: '100%',
                  borderRadius: '6px',
                  variant: 'text',
                  color: !!diagnosisItem?.date ? '#1e293b' : '#9ca3af',
                  border: '1px solid rgba(67, 56, 202, 0.15)',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  justifyContent: 'flex-start',
                  px: 1.5,
                  py: 1,
                  '&:hover': {
                    borderColor: 'rgba(67, 56, 202, 0.3)',
                    backgroundColor: 'rgba(67, 56, 202, 0.05)',
                  },
                  transition: 'all 0.2s ease',
                },
                disableRipple: false,
              }}
              placeholder="Date"
              date={diagnosisItem?.date}
              onChange={(val) => {
                handleChangeDiagnosis(
                  dayjs(val).format(MOMENT_FORMATS.formatThatServerSends),
                  'date',
                  diagnosisRowIndex,
                )
              }}
            />
            {diagnosisItem?.date && (
              <Close
                sx={{
                  position: 'absolute',
                  right: '10px',
                  zIndex: 2,
                  cursor: 'pointer',
                  color: '#CCC',
                  fontSize: '16px',
                }}
                onClick={() => handleChangeDiagnosis(null, 'date', diagnosisRowIndex)}
              />
            )}
          </div>
          {/* 
      <DatePicker
        value={diagnosisItem?.date ? dayjs(diagnosisItem.date).toDate() : null}
        onChange={(val) => {
          handleChangeDiagnosis(
            dayjs(val).format(MOMENT_FORMATS.formatThatServerSends),
            'date',
            diagnosisRowIndex,
          )
        }}
      /> */}
        </PMPSingleRowBox>
      )}
      {getRenderDiagnosisSubSectionsSet(subSections).has(
        DIAGNOSIS_SUB_SECTION_IDS.diagnosis_for_duration.id,
      ) && (
        <PMPSingleRowBox
          whiteBG
          borderRight
          noPadding
          noFlex
          style={{
            width: getPercentageOfItemForDiagnosisTableCellWidth(
              DIAGNOSIS_SUB_SECTION_IDS.diagnosis_for_duration.id,
              94,
              getRenderDiagnosisSubSectionsSet(subSections),
            ),
          }}
        >
          <DiagnosisDurationSelect
            id="diagnosis_for_duration"
            isDisabled={!diagnosisItem?.value}
            classNamePrefix="diagnosis_for_duration_selection"
            placeholder="Duration"
            isSearchable={true}
            isClearable={true}
            defaultOptions={false}
            loadOptions={loadOptionsDiagnosisDuration}
            getOptionValue={(option) => option?.id}
            getOptionLabel={(option) => option?.title}
            isOptionDisabled={(option) =>
              INITIAL_DIAGNOSIS_OPTION_ARRAY_LIST?.map((o) => o?.id).includes(option?.id)
            }
            value={
              diagnosisItem?.duration
                ? { id: diagnosisItem?.duration, title: diagnosisItem?.duration }
                : null
            }
            onChange={(val) => {
              if (!val) {
                changeDiagnosisItemObject(
                  {
                    duration: val?.id,
                  },
                  diagnosisRowIndex,
                )
                return
              }
              const pastDate = getPastDateFromTimeString(val?.id)

              console.log({ pastDate })
              // handleChangeDiagnosis(
              //   dayjs(pastDate).format(MOMENT_FORMATS.formatThatServerSends),
              //   'date',
              //   diagnosisRowIndex,
              // )
              // if (!diagnosisItem?.date) {
              // }

              changeDiagnosisItemObject(
                {
                  duration: val?.id,
                  date: dayjs(pastDate).format(MOMENT_FORMATS.formatThatServerSends),
                },
                diagnosisRowIndex,
              )

              // handleChangeDiagnosis(val?.id, 'duration', diagnosisRowIndex)
            }}
            additional={
              {
                // page: 1,
                // clinicId: clinicId,
                // clinicBrandId: clinicBrandId,
                // recommendedDiagnosisIds,
                // prescriptionViewSettings,
              }
            }
            styles={{
              ...commonStylesPrescriptionSelect,
              menuPortal: (provided, state) => ({
                ...provided,
                zIndex: 9999,
              }),
              menu: (provided, state) => ({
                ...provided,
                ...commonStylesPrescriptionSelect.menu(provided, state),
                zIndex: 9999, // Ensure menu has high z-index
                position: 'absolute', // Ensure proper positioning
              }),
            }}
            menuPortalTarget={menuPortalTarget || (typeof document !== 'undefined' ? document.body : null)}
            menuPosition={menuPortalTarget ? 'fixed' : undefined}
            components={{ ClearIndicator: () => null }}
          />
        </PMPSingleRowBox>
      )}
      <PMPSingleRowBox style={{ width: '3%' }} whiteBG centered>
        <Tooltip title="Delete diagnosis" arrow>
          <IconButton
            onClick={() => deleteDiagnosis(diagnosisRowIndex)}
            size="small"
            sx={{
              color: '#ef4444',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <DeleteOutline sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </PMPSingleRowBox>
    </PMPSingleRowForDiagnosis>
  )
}

export function getPercentageOfItemForDiagnosisTableCellWidth(
  sectionId,
  availableSpaceInPercentage,
  setOfVisibleItems,
) {
  const weightForThisSection = DIAGNOSIS_TABLE_COLUMN_WEIGHTS?.[sectionId]

  const weightSum = Object.entries(DIAGNOSIS_TABLE_COLUMN_WEIGHTS)
    .filter(([key, value]) => setOfVisibleItems.has(key))
    .reduce((acc, [key, value]) => acc + value, 0)
  return `${(weightForThisSection / weightSum) * availableSpaceInPercentage}%`
}
